import { FakeCaretInput, type FakeCaretInputRef } from '@/components/common/fake-caret-input';
import KeyboardSimple from '@/components/common/keyboard-simple';
import ShopFnbClearButton from '@/components/common/shop-fnb-facility/buttons/clear';
import { BuildingNameType } from '@/enums/building';
import { SHOP_SEARCH_MODE } from '@/enums/shop';
import { useAccessibilityTTS, useOrientation } from '@/hooks';
import { useClickOutsideDetector } from '@/hooks/useClickOutsideDetector';
import { TrackingService } from '@/services/analytics/TrackingService';
import { useConfigStore, useShopStore } from '@/stores';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

interface ShopSearchByTextProps {
    isVerticalFullscreen?: boolean;
}

const ShopSearchByText = ({ isVerticalFullscreen = false }: ShopSearchByTextProps) => {
    const { isHorizontal } = useOrientation();
    const { t } = useTranslation();
    const { speak } = useAccessibilityTTS();
    const [tempSearchText, setTempSearchText] = useState('');
    const searchText = useShopStore((state) => state.searchText);
    const setSearchText = useShopStore((state) => state.setSearchText);
    const updateShopContents = useShopStore((state) => state.updateShopContents);
    const keyboardLanguage = useShopStore((state) => state.keyboardLanguage);
    const setKeyboardLanguage = useShopStore((state) => state.setKeyboardLanguage);
    const buildingType = useConfigStore((state) => state.config.server?.building?.buildingType);
    const language = useConfigStore((state) => state.config.ui.language);
    const voice = useConfigStore((state) => state.config.tts.voice);
    const [isCaretVisible, setIsCaretVisible] = useState(false);

    const virtualKeyboardInputRef = useRef<FakeCaretInputRef>(null);
    const inputWrapperRef = useRef<HTMLDivElement>(null);
    const fakeCaretInputWrapperRef = useRef<HTMLDivElement>(null);

    useClickOutsideDetector(inputWrapperRef, () => {
        setIsCaretVisible(false);
    });

    useEffect(() => {
        setTempSearchText(searchText);
    }, [searchText]);

    useEffect(() => {
        if (language === 'ko') {
            setKeyboardLanguage('ko');
        } else {
            setKeyboardLanguage('en');
        }
    }, [language, setKeyboardLanguage]);

    const handleClear = () => {
        setTempSearchText('');
        setSearchText('');
        TrackingService.clearSearchKeyword();
        updateShopContents(SHOP_SEARCH_MODE.SEARCH_BY_TEXT);
        setTimeout(() => {
            fakeCaretInputWrapperRef.current?.focus();
            setIsCaretVisible(true);
        }, 0);
    };

    const handleInputChange = (value: string) => {
        if (!tempSearchText && value.length === 0) return;
        setTempSearchText(value);
        if (value.length === 0) {
            setSearchText('');
            TrackingService.clearSearchKeyword();
            updateShopContents(SHOP_SEARCH_MODE.SEARCH_BY_TEXT);
        }
    };

    const handleSearchContents = (searchText: string) => {
        setSearchText(searchText);
        const results = updateShopContents(SHOP_SEARCH_MODE.SEARCH_BY_TEXT);

        setTimeout(() => {
            if (results?.length === 0) {
                const emptyElement = document.getElementById('shop-empty-result');
                if (emptyElement) {
                    emptyElement.blur();
                    emptyElement.focus();
                }
            } else {
                speak(t('shop.search.tts.content.search_result'));
                const focusTargetId = voice ? 'shop-first-content' : 'shop-page-count';
                const focusTarget = document.getElementById(focusTargetId);
                if (focusTarget) {
                    focusTarget.blur();
                    focusTarget.focus();
                }
            }
        }, 500);

        if (searchText?.trim()) {
            TrackingService.trackKeyword(searchText.trim());
        }
    };

    return (
        <div
            className={clsx(styles['search-text'], {
                [styles['search-text__horizontal']]: isHorizontal,
                [styles['search-text__vertical']]: !isHorizontal && !isVerticalFullscreen,
                [styles['search-text__vertical-fullscreen']]: isVerticalFullscreen,
            })}
        >
            <div ref={inputWrapperRef} className={styles['search-text__input']} data-region-nav>
                <div
                    ref={fakeCaretInputWrapperRef}
                    className={styles['search-text__input--input-wrapper']}
                    tabIndex={9}
                    data-interactive
                    aria-label={`${t('common.keyboard.tts.search_readonly_area')} ${tempSearchText ? tempSearchText : t('shop.search.text.content.input_placeholder')}`}
                >
                    <FakeCaretInput
                        fullWidth={true}
                        ref={virtualKeyboardInputRef}
                        isCaretVisible={isCaretVisible}
                        tabIndex={-1}
                        type="text"
                        placeholder={t('shop.search.text.content.input_placeholder')}
                        value={tempSearchText}
                        style={{ caretColor: 'transparent' }}
                        onFocus={() => {
                            setIsCaretVisible(true);
                        }}
                    />
                </div>
                <div className={styles['search-text__input--clear-button-wrapper']}>
                    {tempSearchText && (
                        <ShopFnbClearButton
                            data-tts-scope="keyboard"
                            tabIndex={9}
                            role="button"
                            data-interactive
                            aria-label={t('shop.search.tts.content.delete_all_input_value')}
                            className={styles['search-text__input--clear-button']}
                            disabled={!tempSearchText}
                            onFocus={() => {
                                speak(`${t('shop.search.tts.content.delete_all_input_value')}, ${t('common.button.tts.button')}`);
                            }}
                            onClick={() => {
                                handleClear();
                            }}
                        />
                    )}
                </div>
            </div>
            <KeyboardSimple
                tabIndex={9}
                value={tempSearchText}
                onInputChange={(value) => {
                    handleInputChange(value);
                    setIsCaretVisible(true);
                    virtualKeyboardInputRef.current?.updateCaretPosition();
                }}
                onSearch={() => handleSearchContents(tempSearchText)}
                keyboardLanguage={keyboardLanguage}
                onLanguageChange={setKeyboardLanguage}
                buildingType={buildingType || BuildingNameType.MALL}
            />
        </div>
    );
};

export default ShopSearchByText;
